// i18n entry point. Currently only English (base) — Sprint C PR (ae) scaffold.
//
// Usage:
//   import { t } from '../i18n';
//   return <h1>{t.notFound.heading}</h1>;
//
// For interpolation (`{path}` placeholders), use the `format()` helper.
//
// When a second locale lands (V1.1+), this module reads a stored locale
// preference + falls back to navigator.language; the imported `t` is then
// the merged-locale tree. Component call-sites stay unchanged.

import { en } from './en';
import type { StringCatalog } from './en';

export const t: StringCatalog = en;
export type { StringCatalog };

type CatalogTree = {
  readonly [key: string]: string | CatalogTree;
};

type PluralCategory = ReturnType<Intl.PluralRules['select']>;

type PluralForms = Partial<Record<PluralCategory, string>> & {
  other: string;
};

const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ps', 'ur', 'yi']);

/**
 * Resolves a dot-path catalog key. Missing or non-string values return the
 * key itself so untranslated copy is visible instead of rendering blank.
 */
export function translate(key: string, catalog: CatalogTree = t as CatalogTree): string {
  const value = key.split('.').reduce<string | CatalogTree | undefined>(
    (node, segment) => {
      if (typeof node !== 'object' || node === null) return undefined;
      return node[segment];
    },
    catalog,
  );
  return typeof value === 'string' && value.length > 0 ? value : key;
}

/**
 * Substitutes `{key}` placeholders in a template string with values from
 * the `vars` map. Missing keys are left as `{key}` for visibility.
 */
export function format(
  template: string,
  vars: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in vars ? String(vars[key]) : match,
  );
}

export function formatDateTime(
  value: Date | number | string,
  locale?: string | string[],
  options: Intl.DateTimeFormatOptions = {
    dateStyle: 'medium',
    timeStyle: 'short',
  },
): string {
  return new Intl.DateTimeFormat(locale, options).format(new Date(value));
}

export function pluralize(
  locale: string | string[] | undefined,
  count: number,
  forms: PluralForms,
): string {
  const category = new Intl.PluralRules(locale).select(count);
  const template = forms[category] ?? forms.other;
  return format(template, { count });
}

export function textDirection(locale?: string): 'ltr' | 'rtl' {
  const source =
    locale || (typeof navigator !== 'undefined' ? navigator.language : 'en');
  const language = source.split('-')[0]?.toLowerCase();
  return language && RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';
}
